import { DEVICE, MARKET_AUDIENCE } from "../../utils/constants";
import { graphql, useStaticQuery } from "gatsby";

import { Box } from "@vinomofo/components";
import { HeaderNarrow } from "./HeaderNarrow";
import { HeaderWide } from "./HeaderWide";
import { Media } from "gatsby-plugin-fresnel";
import { MofoPromises } from "../mofo-promises/MofoPromises";
import { isWhiskymofo, isWhiskymofoSignupPage } from "../../utils/is-page";
import { storefrontNavigationItemProps } from "../../types/vino-data-types";
import styled from "styled-components";
import { useIsWinePage } from "../../hooks/use-is-wine-page";
import { getAssetType } from "../../utils/media-helpers";
import { useLocation } from "@reach/router";

interface NavigationLink {
  name: string;
  nameMobile: string;
  link: {
    url: string;
  };
  device: Array<"desktop" | "mobile">;
}

interface HeaderBackgroundImageProps {
  $backgroundImage: string;
  $videoUrl?: string;
}

const HeaderBackgroundImage = styled(Box)<HeaderBackgroundImageProps>`
  background: linear-gradient(rgba(0, 0, 0, 0.25), rgba(0, 0, 0, 0.25)),
    url(${(props) => props.$backgroundImage});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  overflow: hidden;

  video {
    position: absolute;
    top: 50%;
    left: 50%;
    min-width: 100%;
    min-height: 100%;
    width: auto;
    height: auto;
    transform: translateX(-50%) translateY(-50%);
    z-index: -1;
    object-fit: cover;
  }

  @media ${({ theme }) => theme.mq.max.mobile} {
    @media (min-aspect-ratio: 1/1) {
      height: 100vw;
    }

    @media (orientation: landscape) {
      height: 100vw;
    }
  }
`;

export const Header = ({ bgImage = "" }: { bgImage?: string }): JSX.Element => {
  const isWinePage = useIsWinePage();
  const pageHasBG = bgImage !== "";
  const assetType = getAssetType(bgImage);
  const isVideo = assetType === "video";
  const videoUrl = isVideo ? bgImage : "";
  const { pathname } = useLocation();

  const { vinodata } = useStaticQuery(graphql`
    query {
      vinodata {
        whiskymofoMenu: headerNavigation(name: "Whiskymofo") {
          name
          list {
            name
            nameMobile
            link {
              url
            }
            device
          }
        }
        defaultMenu: headerNavigation {
          name
          list {
            name
            nameMobile
            link {
              url
            }
            device
          }
        }
      }
    }
  `);

  const headerData = isWhiskymofo
    ? vinodata.whiskymofoMenu
    : vinodata.defaultMenu;

  let menuItems: storefrontNavigationItemProps[] =
    headerData?.list?.map((item: NavigationLink) => ({
      link: item.link?.url,
      label: item.name,
      labelMobile: item.nameMobile,
      audience: MARKET_AUDIENCE.ALL,
      device: item.device,
    })) || [];

  if (isWhiskymofoSignupPage(pathname) || isWhiskymofo) {
    menuItems = [];
  }

  return (
    <Box position={"relative"}>
      {bgImage && (
        <HeaderBackgroundImage
          $backgroundImage={!isVideo ? bgImage : ""}
          $videoUrl={isVideo ? videoUrl : undefined}
          position="absolute"
          top={0}
          left={0}
          right={0}
          zIndex={-1}
          height={"100vh"}
          width={"100%"}
          opacity={1}
          display={"block"}
          maxWidth={"1920px"}
          m={"0 auto"}
        >
          {isVideo && (
            <video
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              // poster={bgImage}
            >
              <source src={videoUrl} type="video/mp4" />
              <source src={videoUrl} type="video/webm" />
            </video>
          )}
        </HeaderBackgroundImage>
      )}
      <Media between={["sm", "md"]}>
        <HeaderNarrow menuItems={menuItems} transparentHeader={pageHasBG} />
      </Media>
      <Media between={["md", "lg"]}>
        <HeaderWide
          menuItems={menuItems?.map((item) => ({
            ...item,
            label: item.labelMobile || item.label,
          }))}
          transparentHeader={pageHasBG}
        />
      </Media>
      <Media greaterThanOrEqual="lg">
        <HeaderWide menuItems={menuItems} transparentHeader={pageHasBG} />
      </Media>
      {isWinePage && <MofoPromises />}
    </Box>
  );
};

Header.displayName = "Header";
